import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import apiClient from "../../../../shared/API/apiClient";

import {
  ArrowLeft,
  FlaskConical,
  AlertTriangle,
  ShieldCheck,
  FileCheck2,
  Droplets,
} from "lucide-react";

export default function EffluentDischargeDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const saved = JSON.parse(sessionStorage.getItem("water_filters"));

  const reporting_year_id =
    location.state?.reporting_year_id || saved?.reporting_year_id;

  const legal_entity_id =
    location.state?.legal_entity_id || saved?.legal_entity_id;

  const activity_type_id = 20;

  const [loading, setLoading] = useState(true);
  const [summaryCards, setSummaryCards] = useState({});
  const [dischargeSummary, setDischargeSummary] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!reporting_year_id || !legal_entity_id) return;
    fetchData();
  }, [reporting_year_id, legal_entity_id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get(
        "/api/water-summary/effluent-breakdown",
        {
          params: {
            activity_type_id,
            reporting_year_id,
            legal_entity_id,
          },
        }
      );

      const apiData = response?.data;

      if (apiData) {
        setSummaryCards(apiData?.summary_cards || {});
        setDischargeSummary(apiData?.discharge_summary || []);
        setItems(apiData?.items || []);
      }
    } catch (error) {
      console.error("Effluent Breakdown API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderParameterStatus = (status) => {
    const isWithinRange = status === "within_range";

    return (
      <div className={`eff-status ${isWithinRange ? "success" : "danger"}`}>
        {isWithinRange ? (
          <ShieldCheck size={14} />
        ) : (
          <AlertTriangle size={14} />
        )}

        {isWithinRange ? "Within Range" : "Out of Range"}
      </div>
    );
  };

  const renderRecordStatus = (status) => {
    if (status === "critical") {
      return (
        <div className="eff-top-badge danger">
          <AlertTriangle size={14} />
          Critical
        </div>
      );
    }

    if (status === "non_compliant") {
      return (
        <div className="eff-top-badge warning">
          <AlertTriangle size={14} />
          Non-Compliant
        </div>
      );
    }

    return (
      <div className="eff-top-badge success">
        <ShieldCheck size={14} />
        Compliant
      </div>
    );
  };

  if (loading) {
    return (
      <div className="eff-loader-wrap">
        <div className="eff-loader"></div>
      </div>
    );
  }

  return (
    <div className="dash-page">

      {/* HERO */}
      <div className="eff-hero-card">

        <div className="eff-hero-left">

          <div className="eff-hero-mini">
            <FileCheck2 size={15} />
            DISCHARGE COMPLIANCE
          </div>

          <div className="eff-hero-title">
            Effluent Discharge Breakdown
          </div>

          <div className="eff-hero-subtitle">
            Outlet-wise discharge volume and monitoring overview
          </div>

        </div>

        {/* SUMMARY CARDS */}
        <div className="eff-stat-grid">

          <div className="eff-stat-card">
            <div className="eff-stat-label">
              <Droplets size={15} />
              Total Discharge
            </div>

            <div
  className={`eff-stat-value ${
    summaryCards?.danger ? "eff-stat-value-danger" : ""
  }`}
>
              {summaryCards?.total_discharge || 0}
              <span>{summaryCards?.unit || ""}</span>
            </div>
          </div>

          <div className="eff-stat-card">
            <div className="eff-stat-label">
              Total Outlets
            </div>

            <div
  className={`eff-stat-value ${
    summaryCards?.danger ? "eff-stat-value-danger" : ""
  }`}
>
              {summaryCards?.total_outlets || 0}
            </div>
          </div>

          <div className="eff-stat-card">
            <div className="eff-stat-label">
              Monitoring Records
            </div>

            <div
  className={`eff-stat-value ${
    summaryCards?.danger ? "eff-stat-value-danger" : ""
  }`}
>
              {summaryCards?.monitoring_records || 0}
            </div>
          </div>

          <div className="eff-stat-card">
            <div className="eff-stat-label">
              Non-Compliant Records
            </div>

            <div className="eff-stat-value eff-red-value">
              {summaryCards?.non_compliant_records || 0}/
              {summaryCards?.total_records || 0}
            </div>
          </div>

        </div>

      </div>

      {/* DISCHARGE SUMMARY */}
      <div className="eff-card">

        <div className="eff-card-title">
          Discharge Summary
        </div>

        <table className="eff-table">

          <thead>
            <tr>
              <th>Outlet</th>
              <th>Total Volume (m³)</th>
              <th>Records</th>
              <th>% Share</th>
            </tr>
          </thead>

          <tbody>

            {dischargeSummary?.length > 0 ? (
              <>
                {dischargeSummary.map((row, index) => (
                  <tr key={index}>

                    <td className="eff-outlet">
                      {row?.outlet}
                    </td>

                    <td>{row?.total_volume}</td>

                    <td>{row?.records}</td>

                    <td>{row?.percentage}%</td>

                  </tr>
                ))}

                <tr className="eff-total-row">

                  <td>Total</td>

                  <td>{summaryCards?.total_discharge}</td>

                  <td>{summaryCards?.monitoring_records}</td>

                  <td>100%</td>

                </tr>
              </>
            ) : (
              <tr>
                <td colSpan="4">
                  No summary data found
                </td>
              </tr>
            )}

          </tbody>

        </table>

      </div>

      {/* WATER QUALITY MONITORING */}
      <div className="eff-card">

        <div className="eff-card-title">
          Water Quality Monitoring
        </div>

        <div className="eff-monitoring-subtitle">
          Each outlet may contain multiple monitoring records.
          Compliance is evaluated at the monitoring record level.
        </div>

        {items?.length > 0 ? (
          items.map((item, index) => (

            <div className="eff-outlet-card" key={index}>

              {/* OUTLET HEADER */}
              <div className="eff-outlet-header">

                <div>

                  <div className="eff-outlet-title">
                    {item?.outlet_name}
                  </div>

                  <div className="eff-outlet-meta">

                    <div className="eff-meta-chip">
                      <Droplets size={15} />
                      Total Volume {item?.total_volume} m³
                    </div>

                    <div className="eff-meta-chip">
                      <FlaskConical size={15} />

                      {item?.monitoring_records_count}
                      {" "}
                      Monitoring Record
                      {item?.monitoring_records_count > 1
                        ? "s"
                        : ""}
                    </div>

                  </div>

                </div>

              </div>

              {/* RECORDS */}
              {item?.records?.map((record, rIndex) => (

                <div className="eff-record-card" key={rIndex}>

                  {/* RECORD TOP */}
                  <div className="eff-record-top">

                    <div className="eff-record-left">

                      <div className="eff-record-location">
                        {record?.location || "Unknown Location"}
                      </div>

                      <div className="eff-record-date">
                        {record?.monitoring_date}
                      </div>

                      <div className="eff-record-volume">
                        {record?.volume} m³
                      </div>

                    </div>

                    <div>
                      {renderRecordStatus(record?.status)}
                    </div>

                  </div>

                  {/* PARAMETERS */}
                  {record?.parameters?.length > 0 ? (
                    <>

                      <table className="eff-table eff-parameter-table">

                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                            <th>Unit</th>
                            <th>Permissible</th>
                            <th>Status</th>
                          </tr>
                        </thead>

                        <tbody>

                          {record.parameters.map((param, i) => {

                            const isDanger =
                              param?.status === "out_of_range";

                            return (
                              <tr
                                key={i}
                                className={
                                  isDanger
                                    ? "eff-danger-row"
                                    : ""
                                }
                              >

                                <td className="eff-param-name">
                                  {param?.parameter}
                                </td>

                                <td
                                  className={
                                    isDanger
                                      ? "eff-red-value"
                                      : ""
                                  }
                                >
                                  {param?.value}
                                </td>

                                <td>
                                  {param?.unit || "-"}
                                </td>

                                <td>
                                  {param?.permissible}
                                </td>

                                <td>
                                  {renderParameterStatus(
                                    param?.status
                                  )}
                                </td>

                              </tr>
                            );
                          })}

                        </tbody>

                      </table>

                      {/* SUMMARY MESSAGE */}
                      <div
                        className={
                          record?.status === "compliant"
                            ? "eff-summary-success"
                            : "eff-summary-danger"
                        }
                      >
                        {record?.summary_message}
                      </div>

                      {/* CRITICAL ALERT */}
                      {record?.critical_alert && (
                        <div className="eff-warning-box">
                          <AlertTriangle size={16} />
                          {record?.critical_alert}
                        </div>
                      )}

                    </>
                  ) : (
                    <div className="eff-no-params">
                      No parameter data available.
                    </div>
                  )}

                </div>
              ))}

            </div>
          ))
        ) : (
          <div className="eff-no-params">
            No monitoring records found.
          </div>
        )}

      </div>

      {/* BACK */}
      <div
        className="eff-back"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        Back to Water Summary
      </div>

    </div>
  );
}